import React, { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import AuthService from '../../services/AuthService';
import LeadsAssignedService from '../../services/LeadsAssignedService';
import { Toast } from 'primereact/toast';
import ToastService from '../../services/ToastService';
import LeadService from '../../services/LeadService';
import { LEAD_STATUSES, PROPERTY_TYPES, STATUS_COLORS, getStatusName, getStatusColor } from '../../constants/leadConstants';
import LeadPropertyDealPanel from './LeadPropertyDealPanel';

// Convert API DateTime to datetime-local input value (empty if default/min date)
const toInputDateTime = (raw) => {
  if (!raw) return '';
  const d = new Date(raw);
  if (isNaN(d.getTime()) || d.getFullYear() <= 1) return '';
  // Format: YYYY-MM-DDTHH:mm  (required by datetime-local)
  return d.toISOString().slice(0, 16);
};

const toInputDate = (raw) => {
  if (!raw) return '';
  const d = new Date(raw);
  if (isNaN(d.getTime()) || d.getFullYear() <= 1) return '';
  return d.toISOString().slice(0, 10);
};

const toInputTime = (raw) => {
  if (!raw) return '';
  // TimeSpan comes as "HH:mm:ss" or "HH:mm" — slice first 5 chars
  if (typeof raw === 'string') return raw.slice(0, 5);
  return '';
};



function useQuery() {
    const { search } = useLocation();
    return React.useMemo(() => new URLSearchParams(search), [search]);
}

function EditLeadAssigned() {

const queryParam = useQuery();
const toast = useRef(null);
const toastService = new ToastService(toast); 
const { id } = useParams(); 
const navigate = useNavigate();
const [UserId] = useState(() => AuthService.resolveScopedUserId(queryParam.get("userId")));
const [activeTab, setActiveTab] = useState('edit');
const [leadHistory, setLeadHistory] = useState([]);
const [actualLeadId, setActualLeadId] = useState(null);
const [oldLeadStatusId, setOldLeadStatusId] = useState(null);


const [leadDetails, setLeadDetails] = useState({
  firstName: "",
  leadCode: "",
  email:"",
  contactNumber:"",
  property:"",
});


const [form, setForm] = useState({
    leadId: "",
    leadStatusId: "",
    propertyTypeId: "", 
    userId: UserId, 
    conversationComment:"", 
    leadAssignedDate:"",
    followUpDate:"",
    nextUpdate:"",
    followUpTime:""
  });

  const [formError, setFormError] = useState({});

  const LeadStatus = LEAD_STATUSES;
  const PropertyType = PROPERTY_TYPES;

 
  useEffect(() => {
    const fetchData = async () => {
      if (activeTab === 'leadhistory' && actualLeadId) {
        try {
          // Use actualLeadId (the Lead's own id), not the LeadAssigned row id
          const result = await LeadService.getLeadHistoryByLeadId(actualLeadId);
          setLeadHistory(result);
        } catch (error) {
          console.error('Error fetching lead history:', error);
          toastService.showError("Error", "Error fetching Lead History");
        }
      }
    };

    fetchData();
  }, [activeTab, actualLeadId]);

  useEffect(() => {
    const fetchLeadsAssignedData = async () => {
      try {
        const data = await LeadsAssignedService.getLeadsAssignedById(id);
        setForm({
          ...data,
          leadAssignedDate: toInputDate(data.leadAssignedDate),
          followUpDate:     toInputDateTime(data.followUpDate),
          nextUpdate:       toInputDateTime(data.nextUpdate),
          followUpTime:     toInputTime(data.followUpTime),
        });
        setOldLeadStatusId(data.leadStatusId);
        setActualLeadId(data.leadId); // store the real lead id for history tab

         const leadInfo = await LeadService.getLeadById(data.leadId);
         setLeadDetails({
          firstName: leadInfo.firstName,
             leadCode: leadInfo.leadCode,
             email:leadInfo.email,
             contactNumber:leadInfo.contactNumber,
             property:leadInfo.property,
         });

      } catch (error) {
        console.error('Error fetching leads assigned data:', error);
      } 
    };
  
    fetchLeadsAssignedData();
  }, [id]);
  

  const changeHandler = (e) => {
    const { name, value } = e.target;
    setForm({
      ...form,
      [name]: value,  
    });
  };
  

  const validateForm = () => {
    const errors = {};
    if (!form.conversationComment.trim()) errors.conversationComment = "Please enter the conversation comments.";
    if (!form.leadAssignedDate.trim()) errors.leadAssignedDate = "Please select lead assigned date.";
    if (!form.followUpDate.trim()) errors.followUpDate = "Please select follow up date.";
    if (!form.nextUpdate.trim()) errors.nextUpdate = "Please select next follow update.";
   
    setFormError(errors);

    if (Object.keys(errors).length > 0) {
      toastService.showError("Validation Error", "Please fill out all required fields ");
    }

    return Object.keys(errors).length === 0;
  };

  const onLeadSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }
    updateLeadAssigned();
  };


  const updateLeadAssigned = async () => {
    if (!form.leadStatusId) {
      toastService.showError("Validation Error", "Please select a valid Lead Status.");
      return;
    }

    // Send JSON — controller is [FromBody]. Backend RecordStatusChange already
    // writes the history entry so we must NOT duplicate it here.
    const payload = {
      id:                  parseInt(id),
      leadId:              form.leadId,
      leadStatusId:        parseInt(form.leadStatusId),
      userId:              form.userId,
      propertyTypeId:      parseInt(form.propertyTypeId) || 0,
      conversationComment: form.conversationComment || '',
      leadAssignedDate:    form.leadAssignedDate || new Date().toISOString(),
      followUpDate:        form.followUpDate     || new Date().toISOString(),
      nextUpdate:          form.nextUpdate       || new Date().toISOString(),
      followUpTime:        (form.followUpTime || '00:00:00').replace(/^(\d{2}:\d{2})$/, '$1:00'),
      leadScore:           form.leadScore        || 0,
      createdBy:           form.createdBy        || 0,
      createdOn:           form.createdOn        || new Date().toISOString(),
      modifiedOn:          new Date().toISOString(),
      modifiedBy:          form.modifiedBy       || 0,
      isActive:            form.isActive         ?? true,
    };

    try {
      await LeadsAssignedService.updateLeadAssigned(id, payload);
      setOldLeadStatusId(parseInt(form.leadStatusId));
      toastService.showSuccess("Success", "Lead assignment updated successfully");
      setTimeout(() => navigate(-1), 1000);
    } catch (error) {
      console.error("Error updating lead assigned:", error);
      toastService.showError("Error", "Failed to update lead assignment");
    }
  };
  
  
  
  const getLeadStatusName = (statusId) => getStatusName(statusId);


  /* ─── shared style helpers ─── */
  const lbl = {
    display: 'block', fontSize: '12px', fontWeight: '600',
    color: '#8392ab', marginBottom: '6px',
    textTransform: 'uppercase', letterSpacing: '0.05em',
  };
  const inp = (err, disabled) => ({
    width: '100%', padding: '10px 14px',
    border: `1.5px solid ${err ? '#f5365c' : disabled ? '#f0f2f5' : '#e9ecef'}`,
    borderRadius: '10px', fontSize: '14px',
    color: disabled ? '#adb5bd' : '#344767',
    background: disabled ? '#f8f9fe' : '#fff',
    outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.2s',
    cursor: disabled ? 'not-allowed' : 'auto',
  });
  const errMsg = { fontSize: '11px', color: '#f5365c', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px' };

  const statusColors = STATUS_COLORS;

  const getActivityInfo = (history) => {
    const c = (history.comment || '').toLowerCase();
    const sid = history.leadStatusId;
    if (c.includes('whatsapp') || c.includes(' wa ') || c.includes('wa:'))
      return { icon: 'fa-comment-dots', color: '#25d366', label: 'WhatsApp' };
    if (c.includes('email') || c.includes(' mail'))
      return { icon: 'fa-envelope', color: '#11cdef', label: 'Email' };
    if (c.includes('meeting') || c.includes(' met ') || c.includes('meeting:'))
      return { icon: 'fa-handshake', color: '#344767', label: 'Meeting' };
    if (c.includes('note') || c.includes('remark'))
      return { icon: 'fa-sticky-note', color: '#ffd600', label: 'Note' };
    if (c.includes('call') || c.includes('spoke') || c.includes('rang') || sid === 8 || sid === 5)
      return { icon: 'fa-phone-alt', color: '#fb6340', label: 'Call' };
    if (sid === 9 || c.includes('upcoming') || c.includes('schedule') || c.includes('visit'))
      return { icon: 'fa-calendar-alt', color: '#5e72e4', label: 'Visit' };
    if (sid === 12 || sid === 13)
      return { icon: 'fa-trophy', color: '#2dce89', label: 'Deal' };
    if (c.includes('sms') || c.includes('text'))
      return { icon: 'fa-sms', color: '#825ee4', label: 'SMS' };
    return { icon: 'fa-exchange-alt', color: getStatusColor(sid), label: 'Status Change' };
  };

  const SecHead = ({ color, icon, title }) => (
    <div style={{ paddingBottom: '10px', borderBottom: '1.5px solid #f0f2f5', display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '18px' }}>
      <div style={{ width: '30px', height: '30px', borderRadius: '8px', background: color + '18', display: 'flex', alignItems: 'center', justifyContent: 'center', color, fontSize: '13px' }}>
        <i className={`fas ${icon}`} />
      </div>
      <p style={{ fontSize: '13px', fontWeight: '700', color: '#344767', textTransform: 'uppercase', letterSpacing: '0.06em', margin: 0 }}>{title}</p>
    </div>
  );

  const currentStatusColor = statusColors[form.leadStatusId] || '#8392ab';
  const currentStatusName = getLeadStatusName(Number(form.leadStatusId));

  return (
    <div style={{ padding: '24px', minHeight: '100vh', background: '#f4f6fb' }}>
      <Toast ref={toast} />
      <style>{`
        .ela-input:focus { border-color: #344767 !important; box-shadow: 0 0 0 3px rgba(52,71,103,0.1) !important; }
        .ela-select:focus { border-color: #344767 !important; box-shadow: 0 0 0 3px rgba(52,71,103,0.1) !important; outline: none; }
        .ela-submit:hover { opacity: 0.88; transform: translateY(-1px); }
        .ela-ghost:hover { background: #f0f2f5 !important; }
        .ela-tab-btn { transition: all 0.2s; }
      `}</style>

      {/* Page Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          <div style={{
            width: '48px', height: '48px', borderRadius: '12px',
            background: 'linear-gradient(135deg, #344767 0%, #21243d 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', fontSize: '20px', boxShadow: '0 4px 12px rgba(52,71,103,0.3)',
          }}>
            <i className="fas fa-tasks" />
          </div>
          <div>
            <p style={{ fontSize: '22px', fontWeight: '700', color: '#344767', margin: 0, lineHeight: 1.2 }}>
              Edit Lead Assignment
            </p>
            <p style={{ fontSize: '13px', color: '#8392ab', margin: 0, marginTop: '2px' }}>
              Assignment ID #{id}
            </p>
          </div>
        </div>
        <button
          type="button"
          className="ela-ghost"
          onClick={() => navigate(-1)}
          style={{
            background: '#fff', border: '1.5px solid #e9ecef', borderRadius: '10px',
            padding: '9px 20px', fontSize: '13px', fontWeight: '600', color: '#525f7f',
            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '7px', transition: 'all 0.2s',
          }}
        >
          <i className="fas fa-arrow-left" />Back
        </button>
      </div>

      {/* Lead Identity Banner */}
      {leadDetails.firstName && (
        <div style={{
          background: 'linear-gradient(135deg, #344767 0%, #21243d 100%)',
          borderRadius: '16px', padding: '18px 24px', marginBottom: '24px',
          display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap',
          boxShadow: '0 4px 20px rgba(52,71,103,0.2)',
        }}>
          <div style={{
            width: '48px', height: '48px', borderRadius: '50%', flexShrink: 0,
            background: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center',
            justifyContent: 'center', color: '#fff', fontSize: '18px', fontWeight: '700',
          }}>
            {(leadDetails.firstName || '?')[0].toUpperCase()}
          </div>
          <div style={{ flex: 1, minWidth: '180px' }}>
            <p style={{ color: '#fff', fontWeight: '700', fontSize: '16px', margin: 0 }}>{leadDetails.firstName}</p>
            <p style={{ color: 'rgba(255,255,255,0.65)', fontSize: '13px', margin: 0 }}>
              {leadDetails.email || 'No email'}&nbsp;·&nbsp;{leadDetails.contactNumber || 'No contact'}
            </p>
          </div>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            {leadDetails.leadCode && (
              <span style={{ background: 'rgba(255,255,255,0.15)', color: '#fff', borderRadius: '20px', padding: '4px 14px', fontSize: '12px', fontWeight: '600' }}>
                <i className="fas fa-hashtag" style={{ marginRight: '5px' }} />{leadDetails.leadCode}
              </span>
            )}
            {form.leadStatusId && (
              <span style={{ background: currentStatusColor + '33', color: '#fff', borderRadius: '20px', padding: '4px 14px', fontSize: '12px', fontWeight: '600', border: `1px solid ${currentStatusColor}66` }}>
                <i className="fas fa-circle" style={{ fontSize: '8px', marginRight: '6px' }} />{currentStatusName}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Card with Tabs */}
      <div style={{ background: '#fff', borderRadius: '20px', boxShadow: '0 4px 24px rgba(52,71,103,0.08)', overflow: 'hidden' }}>

        {/* Tab bar */}
        <div style={{ display: 'flex', borderBottom: '2px solid #f0f2f5', padding: '0 24px', background: '#fff' }}>
          {[
            { key: 'edit', icon: 'fa-edit', label: 'Edit Assignment' },
            { key: 'leadhistory', icon: 'fa-history', label: 'Lead History' },
            { key: 'crm', icon: 'fa-building', label: 'Properties & Deals' },
          ].map(({ key, icon, label }) => {
            const isActive = activeTab === key;
            return (
              <button
                key={key}
                type="button"
                className="ela-tab-btn"
                onClick={() => setActiveTab(key)}
                style={{
                  padding: '16px 20px', border: 'none', background: 'transparent', cursor: 'pointer',
                  fontWeight: isActive ? '700' : '500', fontSize: '14px',
                  color: isActive ? '#344767' : '#8392ab',
                  borderBottom: isActive ? '3px solid #344767' : '3px solid transparent',
                  marginBottom: '-2px', display: 'flex', alignItems: 'center', gap: '7px',
                  transition: 'all 0.2s',
                }}
              >
                <i className={`fas ${icon}`} style={{ fontSize: '13px' }} />{label}
              </button>
            );
          })}
        </div>

        {/* ── EDIT TAB ── */}
        {activeTab === 'edit' && (
          <form onSubmit={onLeadSubmit}>
            <div style={{ padding: '28px' }}>

              {/* Lead Info (read-only) */}
              <div style={{ marginBottom: '28px' }}>
                <SecHead color="#8392ab" icon="fa-id-badge" title="Lead Information (Read-only)" />
                <div className="row g-3">
                  {[
                    { label: 'Lead Code', value: leadDetails.leadCode },
                    { label: 'Lead Name', value: leadDetails.firstName },
                    { label: 'Email', value: leadDetails.email },
                    { label: 'Contact Number', value: leadDetails.contactNumber },
                    { label: 'Property Interest', value: leadDetails.property },
                  ].map(({ label, value }) => (
                    <div className="col-md-4" key={label}>
                      <label style={lbl}>{label}</label>
                      <input
                        type="text" readOnly disabled
                        style={inp(false, true)}
                        value={value || '—'}
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Dates */}
              <div style={{ marginBottom: '28px' }}>
                <SecHead color="#5e72e4" icon="fa-calendar-alt" title="Dates & Schedule" />
                <div className="row g-3">
                  <div className="col-md-4">
                    <label style={lbl}>
                      Lead Assigned Date
                      <span style={{ marginLeft: '6px', fontSize: '10px', color: '#adb5bd', textTransform: 'none', letterSpacing: 0 }}>(read-only)</span>
                    </label>
                    <input
                      type="text" name="leadAssignedDate" id="leadAssignedDate"
                      style={inp(!!formError.leadAssignedDate, true)}
                      value={form.leadAssignedDate || ''} onChange={changeHandler} disabled
                    />
                    {formError.leadAssignedDate && <p style={errMsg}><i className="fas fa-exclamation-circle" />{formError.leadAssignedDate}</p>}
                  </div>
                  <div className="col-md-4">
                    <label style={lbl}>Follow Up Date</label>
                    <input
                      type="datetime-local" name="followUpDate" id="followUpDate"
                      className="ela-input"
                      style={inp(!!formError.followUpDate, false)}
                      value={form.followUpDate || ''} onChange={changeHandler}
                    />
                    {formError.followUpDate && <p style={errMsg}><i className="fas fa-exclamation-circle" />{formError.followUpDate}</p>}
                  </div>
                  <div className="col-md-4">
                    <label style={lbl}>Next Follow Up Date</label>
                    <input
                      type="datetime-local" name="nextUpdate" id="nextUpdate"
                      className="ela-input"
                      style={inp(!!formError.nextUpdate, false)}
                      value={form.nextUpdate || ''} onChange={changeHandler}
                    />
                    {formError.nextUpdate && <p style={errMsg}><i className="fas fa-exclamation-circle" />{formError.nextUpdate}</p>}
                  </div>
                  <div className="col-md-4">
                    <label style={lbl}>Follow Up Time</label>
                    <input
                      type="time" name="followUpTime" id="followUpTime"
                      className="ela-input"
                      style={inp(!!formError.followUpTime, false)}
                      value={form.followUpTime || ''} onChange={changeHandler}
                    />
                    {formError.followUpTime && <p style={errMsg}><i className="fas fa-exclamation-circle" />{formError.followUpTime}</p>}
                  </div>
                </div>
              </div>

              {/* Status & Classification */}
              <div style={{ marginBottom: '28px' }}>
                <SecHead color="#2dce89" icon="fa-tag" title="Status & Classification" />
                <div className="row g-3">
                  <div className="col-md-4">
                    <label style={lbl}>Lead Status</label>
                    <select
                      name="leadStatusId" id="leadStatusId"
                      className="ela-select"
                      style={{ ...inp(!!formError.leadStatusId, false), appearance: 'none', cursor: 'pointer' }}
                      value={form.leadStatusId} onChange={changeHandler}
                    >
                      <option value="">— Select Status —</option>
                      {LeadStatus.map(({ id, name }) => (
                        <option key={id} value={id}>{name}</option>
                      ))}
                    </select>
                    {formError.leadStatusId && <p style={errMsg}><i className="fas fa-exclamation-circle" />{formError.leadStatusId}</p>}
                  </div>
                  <div className="col-md-4">
                    <label style={lbl}>Property Type</label>
                    <select
                      name="propertyTypeId" id="propertyTypeId"
                      className="ela-select"
                      style={{ ...inp(!!formError.propertyTypeId, false), appearance: 'none', cursor: 'pointer' }}
                      value={form.propertyTypeId} onChange={changeHandler}
                    >
                      <option value="">— Select Property —</option>
                      {PropertyType.map(({ id, name }) => (
                        <option key={id} value={id}>{name}</option>
                      ))}
                    </select>
                    {formError.propertyTypeId && <p style={errMsg}><i className="fas fa-exclamation-circle" />{formError.propertyTypeId}</p>}
                  </div>
                </div>
              </div>

              {/* Conversation Notes */}
              <div>
                <SecHead color="#fb6340" icon="fa-comment-dots" title="Conversation Notes" />
                <div className="row g-3">
                  <div className="col-12">
                    <label style={lbl}>Conversation Comments</label>
                    <textarea
                      rows="4" name="conversationComment" id="conversationComment"
                      className="ela-input"
                      style={{ ...inp(!!formError.conversationComment, false), resize: 'vertical', minHeight: '100px' }}
                      placeholder="Enter conversation details, client interest, key points…"
                      value={form.conversationComment || ''} onChange={changeHandler}
                    />
                    {formError.conversationComment && <p style={errMsg}><i className="fas fa-exclamation-circle" />{formError.conversationComment}</p>}
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div style={{
              padding: '20px 28px', background: '#f8f9fe', borderTop: '1.5px solid #f0f2f5',
              display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '12px',
            }}>
              <button
                type="button" onClick={() => navigate(-1)}
                style={{
                  background: 'transparent', border: '1.5px solid #e9ecef', borderRadius: '10px',
                  padding: '10px 24px', fontSize: '14px', fontWeight: '600', color: '#8392ab', cursor: 'pointer',
                }}
              >
                <i className="fas fa-times" style={{ marginRight: '7px' }} />Cancel
              </button>
              <button
                type="submit"
                className="ela-submit"
                style={{
                  background: 'linear-gradient(135deg, #344767 0%, #21243d 100%)',
                  border: 'none', borderRadius: '10px', padding: '10px 32px',
                  fontSize: '14px', fontWeight: '700', color: '#fff', cursor: 'pointer',
                  boxShadow: '0 4px 15px rgba(52,71,103,0.3)',
                  display: 'flex', alignItems: 'center', gap: '8px', transition: 'all 0.2s',
                }}
              >
                <i className="fas fa-save" />Save Changes
              </button>
            </div>
          </form>
        )}

        {/* ── LEAD HISTORY TAB ── */}
        {activeTab === 'leadhistory' && (
          <div style={{ padding: '28px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px', flexWrap: 'wrap', gap: '10px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ width: '30px', height: '30px', borderRadius: '8px', background: '#5e72e418', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#5e72e4', fontSize: '13px' }}>
                  <i className="fas fa-history" />
                </div>
                <p style={{ fontWeight: '700', color: '#344767', fontSize: '15px', margin: 0 }}>Activity Timeline</p>
              </div>
              {leadHistory.length > 0 && (
                <span style={{ background: '#e8f5e9', color: '#2dce89', borderRadius: '20px', padding: '4px 14px', fontSize: '12px', fontWeight: '700' }}>
                  {leadHistory.length} records
                </span>
              )}
            </div>

            {leadHistory.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '52px 24px' }}>
                <i className="fas fa-history" style={{ fontSize: '44px', color: '#e9ecef', display: 'block', marginBottom: '14px' }} />
                <p style={{ fontWeight: '600', color: '#525f7f', margin: '0 0 6px' }}>No history yet</p>
                <p style={{ fontSize: '13px', color: '#8392ab', margin: 0 }}>History entries will appear here after lead status updates</p>
              </div>
            ) : (
              <div style={{ position: 'relative' }}>
                {/* vertical line */}
                <div style={{ position: 'absolute', left: '19px', top: '8px', bottom: '8px', width: '2px', background: '#e9ecef', borderRadius: '2px' }} />
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
                  {[...leadHistory]
                    .sort((a, b) => new Date(b.createdOn || b.followUpDate || 0) - new Date(a.createdOn || a.followUpDate || 0))
                    .map((history, index) => {
                    const sc = statusColors[history.leadStatusId] || '#8392ab';
                    const sName = getLeadStatusName(history.leadStatusId);
                    const act = getActivityInfo(history);
                    const isLast = index === leadHistory.length - 1;
                    return (
                      <div key={index} style={{ display: 'flex', gap: '20px', paddingBottom: isLast ? '0' : '24px' }}>
                        {/* activity-type dot */}
                        <div style={{ flexShrink: 0, position: 'relative', zIndex: 1 }}>
                          <div style={{
                            width: '40px', height: '40px', borderRadius: '50%',
                            background: act.color + '18', border: `2px solid ${act.color}`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            color: act.color, fontSize: '14px',
                          }}>
                            <i className={`fas ${act.icon}`} />
                          </div>
                        </div>
                        {/* card */}
                        <div style={{
                          flex: 1, background: '#f8f9fe', borderRadius: '14px',
                          padding: '16px 20px', border: '1.5px solid #f0f2f5',
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px', flexWrap: 'wrap', gap: '8px' }}>
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                              {/* activity type badge */}
                              <span style={{
                                background: act.color + '18', color: act.color,
                                borderRadius: '20px', padding: '3px 10px',
                                fontSize: '11px', fontWeight: '700',
                              }}>
                                <i className={`fas ${act.icon}`} style={{ marginRight: '5px' }} />{act.label}
                              </span>
                              {/* status badge */}
                              <span style={{
                                background: sc + '18', color: sc,
                                borderRadius: '20px', padding: '3px 10px',
                                fontSize: '11px', fontWeight: '600',
                              }}>
                                {sName}
                              </span>
                            </div>
                            <span style={{ fontSize: '12px', color: '#8392ab' }}>
                              {history.createdOn && new Date(history.createdOn).getFullYear() > 1
                                ? new Date(history.createdOn).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                                : `Entry ${index + 1}`}
                            </span>
                          </div>

                          {history.comment && (
                            <div style={{ display: 'flex', gap: '10px', marginBottom: '12px', alignItems: 'flex-start' }}>
                              <i className="fas fa-comment-dots" style={{ color: '#8392ab', marginTop: '2px', fontSize: '13px', flexShrink: 0 }} />
                              <p style={{ margin: 0, fontSize: '14px', color: '#344767', lineHeight: 1.5 }}>{history.comment}</p>
                            </div>
                          )}

                          <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                            {history.followUpDate && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                                <div style={{ width: '26px', height: '26px', borderRadius: '7px', background: '#5e72e418', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                  <i className="fas fa-calendar-check" style={{ color: '#5e72e4', fontSize: '11px' }} />
                                </div>
                                <div>
                                  <p style={{ fontSize: '10px', color: '#8392ab', margin: 0, fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Follow Up</p>
                                  <p style={{ fontSize: '13px', color: '#344767', margin: 0, fontWeight: '600' }}>
                                    {new Date(history.followUpDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                                  </p>
                                </div>
                              </div>
                            )}
                            {history.nextUpdate && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                                <div style={{ width: '26px', height: '26px', borderRadius: '7px', background: '#2dce8918', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                  <i className="fas fa-forward" style={{ color: '#2dce89', fontSize: '11px' }} />
                                </div>
                                <div>
                                  <p style={{ fontSize: '10px', color: '#8392ab', margin: 0, fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Next Follow Up</p>
                                  <p style={{ fontSize: '13px', color: '#344767', margin: 0, fontWeight: '600' }}>
                                    {new Date(history.nextUpdate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                                  </p>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── CRM: inventory matching, buyer, deals ── */}
        {activeTab === 'crm' && (
          actualLeadId ? (
            <LeadPropertyDealPanel
              leadId={actualLeadId}
              leadAssignedId={id ? parseInt(id, 10) : undefined}
              toastService={toastService}
            />
          ) : (
            <div style={{ padding: '48px', textAlign: 'center', color: '#8392ab', fontSize: '14px' }}>
              <i className="fas fa-circle-notch fa-spin" style={{ marginRight: '8px' }} />
              Loading lead…
            </div>
          )
        )}
      </div>
    </div>
  );
}

export default EditLeadAssigned